import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";

import { ApiError, requestPasswordReset } from "../api/client";
import { RequiredMark } from "../ui/required-mark";

/**
 * Ask for a password-reset link.
 *
 * ── Why this is new, when reset shipped on 2026-08-31 ───────────────────────
 *
 * Because only the *server* shipped. STORY-063b built both endpoints and the
 * email, and `passwordResetUrl` has been mailing links to `/reset/:token` ever
 * since — to an application that had no such route and no way to ask for one.
 * A person who forgot their password had no button to press, and if an operator
 * triggered a reset for them by hand, the link in their inbox opened a blank
 * page. Both halves are here now.
 *
 * ── The response is always the same, and the screen has to hold that line ───
 *
 * The API answers 204 whether or not the address belongs to an account, on
 * purpose: anything else turns this into a way to ask *does this person work at
 * this practice*, which for a medical clinic is a real disclosure. So this
 * screen must not say "sent!" for one address and "unknown" for another — it
 * says the same sentence either way, and words it so that is honest rather than
 * evasive.
 */
export function ForgotPasswordPage(): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await requestPasswordReset(email.trim());
      setSent(true);
    } catch (caught) {
      // Only a rate limit or an unreachable server gets here — the endpoint
      // does not fail on an unknown address.
      setError(caught instanceof ApiError ? caught.message : "Could not reach the server.");
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <main className="sheet-wide">
        <h1>Check your inbox</h1>
        <p data-testid="forgot-sent">
          If <strong>{email}</strong> belongs to a DentaCore account, a link to choose a new
          password is on its way. It works once and expires in an hour.
        </p>
        {/*
          **No longer tells anybody to go and find their subdomain** (QA20-7).
          It used to, and it was wrong twice over: asking at the apex genuinely
          sent nothing, so the advice was the only thing standing between the
          reader and a screen that had lied to them — and the person reading it
          has forgotten their password, which is the worst moment to ask them to
          remember an address instead. The server resolves the practice from the
          address now, exactly as signing in does.
        */}
        <p>
          Nothing arrived? Check the address you typed, and look in your spam folder. If you work at
          more than one practice, one link covers all of them — your DentaCore password is the same
          everywhere.
        </p>
        <p>
          <Link to="/login">Back to sign in</Link>
        </p>
      </main>
    );
  }

  return (
    <main className="sheet-wide">
      <h1>Forgotten password</h1>
      <p>Tell us your email address and we will send you a link to choose a new one.</p>

      <form onSubmit={(event) => void handleSubmit(event)} noValidate>
        <label htmlFor="forgot-email">
          Email
          <RequiredMark />
        </label>
        <input
          id="forgot-email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          data-testid="forgot-email"
        />

        {error !== null && (
          <p role="alert" className="error" data-testid="forgot-error">
            {error}
          </p>
        )}

        <button type="submit" disabled={submitting} data-testid="forgot-submit">
          {submitting ? "Sending…" : "Send me a link"}
        </button>
      </form>

      <p>
        <Link to="/login">Back to sign in</Link>
      </p>
    </main>
  );
}
